/**
 * Searching every safety recommendation in the local copy, from every source.
 *
 * <p>Read only: this screen finds and reads recommendations, it never changes them. They arrive
 * through a source connector's sync job and nothing here, or anywhere else in the UI, creates or
 * edits one.</p>
 *
 * <p>Searching happens on the server rather than by filtering in the browser, since the whole copy
 * is far too large to hold in a tab. Recommendations are read from the FAA's side: its own status,
 * how long it has been open, and why it stands where it does. A chat sits over whatever the search
 * currently shows, and a row opens that recommendation's own tab.</p>
 *
 * <p>The workbook has its own register over the recommendations tied to one workbook's reports;
 * the two share how a recommendation reads and how a row looks, not how the set is found.</p>
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Fab,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import {
    SafetyRecommendationDTO,
    SafetyRecommendationLetterDTO,
    SafetyRecommendationStatsDTO,
    SafetyRecommendationStatusGroup,
    DeclaredChainsDTO,
    SafetyRecommendationStepFilter,
    SafetyRecommendationTaxonomyDTO,
    RepeatedAskGroupDTO,
    RepeatedAskOrder,
    SafetyRecommendationTaxonomySummaryDTO,
} from '../../../../../@types/safety-recommendation';
import {ResourceType} from '../../../../../@types/resource-type';
import {useWorkspaceContext} from '../../../../../context/workspaceContext';
import {useDebounce} from '../../../../../hooks/useDebounce';
import {
    DEFAULT_REGISTER_FILTERS,
    RegisterFilters,
    useRegisterFilters,
    useRegisterStore,
} from '../../../../../stores/safety-recommendation-register.store';
import AgentChatPanel, {AgentClient} from '../../../../common/agent/AgentChatPanel';
import {StepDTO} from '../../../../../@types/agent-investigation';
import DeclaredChains, {DeclaredChainKind} from '../../../../safety-recommendations/DeclaredChains';
import RecommendationOverview, {RecommendationStatusChip, STATUS_STYLE} from '../../../../safety-recommendations/RecommendationOverview';
import TopicOverTime from '../../../../safety-recommendations/TopicOverTime';
import RepeatedAsks from '../../../../safety-recommendations/RepeatedAsks';
import RecommendationSummary from '../../../../safety-recommendations/RecommendationSummary';
import CorrespondenceDialog from '../../../../safety-recommendations/CorrespondenceDialog';
import Tooltip from '@mui/material/Tooltip';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import safetyRecommendationService from '../../../../../services/safety-recommendation.service';
import {
    ageYears,
    ANY_STATUS,
    countsToIssuedByYear,
    displayStatus,
    emptyGroupRecord,
    focusStatus,
    groupsForSelection,
    isAddressedToFaa,
    isOpen,
    IssuedByYear,
    MIXED_STATUS,
    STATUS_GROUP_LABELS,
    STATUS_GROUP_ORDER,
    STATUS_PRESETS,
    statusSelection,
} from '../../../../safety-recommendations/recommendations';

/** The register table never gets shorter than this; a shorter window scrolls the whole tab. */
const MIN_TABLE_HEIGHT = 420;
/** A page of the register. Kept at or under the letter limit so every search carries its letters. */
/** More groups than the corpus can hold, so an export gets all of them. */
const EVERY_REPEATED_ASK = 5000;

const PAGE_SIZE = 25;
/** A keystroke should not be a search: the text filter waits this long for the typing to stop. */
const SEARCH_DEBOUNCE_MS = 300;

const SUGGESTED_PROMPTS = [
    'Which of these has the FAA left open longest, and what did the NTSB last say about each?',
    'Where did the NTSB judge the FAA\'s action unacceptable, and why?',
    'What themes run through these recommendations?',
];

/**
 * The register takes no props: there is one of it, and what it shows lives in its store, which the
 * explorer writes to when a source row is clicked.
 */
const SafetyRecommendationSearchEditor: React.FC = () => {
    const filters = useRegisterFilters();
    const setFilters = useRegisterStore((state) => state.setFilters);
    const agentTurns = useRegisterStore((state) => state.agentTurns);
    const setAgentTurns = useRegisterStore((state) => state.setAgentTurns);
    const {setCurrentWorkspaceObject} = useWorkspaceContext();

    const [rows, setRows] = useState<SafetyRecommendationDTO[] | null>(null);
    const [total, setTotal] = useState(0);
    const [stats, setStats] = useState<SafetyRecommendationStatsDTO | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);
    const [chatOpen, setChatOpen] = useState(false);
    const [chatFullscreen, setChatFullscreen] = useState(false);
    const [chainsKind, setChainsKind] = useState<DeclaredChainKind | null>(null);
    const [chains, setChains] = useState<DeclaredChainsDTO | null>(null);
    const [chainsError, setChainsError] = useState<string | null>(null);
    // Static reference data, so it is read once and never again.
    const [taxonomy, setTaxonomy] = useState<SafetyRecommendationTaxonomyDTO | null>(null);
    const today = useMemo((): Date => new Date(), []);

    // Typed filters wait for the typing to stop; everything chosen with a click searches at once.
    // A year is typed a digit at a time, so without this "2020" searches for the years 2, 20 and 202
    // on its way, and each of those is a full search and count.
    const debouncedText = useDebounce(filters.text, SEARCH_DEBOUNCE_MS);
    const debouncedFromYear = useDebounce(filters.issuedFromYear, SEARCH_DEBOUNCE_MS);
    const debouncedToYear = useDebounce(filters.issuedToYear, SEARCH_DEBOUNCE_MS);

    useEffect(() => {
        let current = true;
        safetyRecommendationService.getTaxonomy()
            .then((loaded): void => { if (current) setTaxonomy(loaded); })
            // A filter nobody can use is better than a screen that will not open: the search works
            // without it.
            .catch((): void => { if (current) setTaxonomy(null); });
        return (): void => { current = false; };
    }, []);

    /** Every classification, flattened for the filter, each carrying its category for grouping. */
    const taxonomyOptions = useMemo((): {leafId: number; leaf: string; category: string; isamDefinition: string}[] =>
        (taxonomy?.categories ?? []).flatMap((category) => category.leaves.map((leaf) => ({
            leafId: leaf.id,
            leaf: leaf.name,
            category: category.name,
            isamDefinition: leaf.isamDefinition,
        }))), [taxonomy]);

    const searching = debouncedText.trim().length > 0;

    // One search and one count for the filters as they stand. A later answer for an older search is
    // dropped by its request number, so a slow first answer cannot overwrite a newer one.
    const request = useRef(0);
    const {statusGroups, addressedTo, includeSuperseded, sources, sortBy, sortDirection, page, taxonomyLeafIds} = filters;
    /**
     * Relevance only means something while there is text, so the default resolves to it while
     * searching and to issue date otherwise. A column the user clicked is never overridden, which
     * is why the default is its own value rather than issuedDate standing in for it.
     */
    const effectiveSort = sortBy === 'default' ? (searching ? 'relevance' : 'issuedDate') : sortBy;
    const criteria = useMemo(() => ({
        text: debouncedText,
        statusGroups,
        addressedTo,
        issuedFromYear: debouncedFromYear,
        issuedToYear: debouncedToYear,
        includeSuperseded,
        sources,
        taxonomyLeafIds,
        // A page this size can carry its letters, so the chat always has the assessments.
        includeLetters: true,
    }), [debouncedText, statusGroups, addressedTo, debouncedFromYear, debouncedToYear, includeSuperseded,
        sources, taxonomyLeafIds]);

    useEffect(() => {
        const current = request.current + 1;
        request.current = current;
        setError(null);
        // Two requests, each standing on its own: a count that fails leaves the table showing what
        // matched, with empty charts, rather than replacing the results with an error.
        safetyRecommendationService.search(criteria, page, PAGE_SIZE, `${effectiveSort},${sortDirection}`)
            .then((found): void => {
                if (request.current !== current) return;
                setRows(found.content);
                setTotal(found.page.totalElements);
            })
            .catch((failure: Error): void => {
                if (request.current !== current) return;
                setRows(null);
                setError(failure.message || 'Failed to search recommendations');
            });
        safetyRecommendationService.summary(criteria)
            .then((counts): void => {
                if (request.current === current) setStats(counts);
            })
            .catch((): void => {
                if (request.current === current) setStats(null);
            });
    }, [criteria, effectiveSort, sortDirection, page, attempt]);

    // The topic grid: a wider aggregate than the charts, so it is fetched only while it is open.
    const [topicOpen, setTopicOpen] = useState(false);
    const [topicSummary, setTopicSummary] = useState<SafetyRecommendationTaxonomySummaryDTO | null>(null);
    const [topicError, setTopicError] = useState<string | null>(null);
    useEffect(() => {
        if (!topicOpen) return;
        let current = true;
        setTopicError(null);
        safetyRecommendationService.taxonomySummary(criteria)
            .then((counts): void => { if (current) setTopicSummary(counts); })
            .catch((failure: Error): void => {
                if (!current) return;
                setTopicSummary(null);
                setTopicError(failure.message || 'Failed to count recommendations by taxonomy');
            });
        return (): void => { current = false; };
    }, [topicOpen, criteria]);

    // Repeated asks are a property of the corpus rather than of a search, so they are loaded once
    // the panel is opened and not refetched as the filters change.
    const [repeatsOpen, setRepeatsOpen] = useState(false);
    const [repeats, setRepeats] = useState<RepeatedAskGroupDTO[] | null>(null);
    const [repeatsOrder, setRepeatsOrder] = useState<RepeatedAskOrder>('NEWEST');
    const [repeatsError, setRepeatsError] = useState<string | null>(null);
    useEffect(() => {
        if (!repeatsOpen) return;
        let current = true;
        setRepeats(null);
        setRepeatsError(null);
        safetyRecommendationService.repeatedAsks(repeatsOrder)
            .then((found): void => { if (current) setRepeats(found); })
            .catch((failure: Error): void => {
                if (current) setRepeatsError(failure.message || 'Failed to load repeated asks');
            });
        return (): void => { current = false; };
    }, [repeatsOpen, repeatsOrder]);

    /** Everything, not just the screenful on show, which is what an export is for. */
    const exportRepeats = (): Promise<RepeatedAskGroupDTO[]> =>
        safetyRecommendationService.repeatedAsks(repeatsOrder, EVERY_REPEATED_ASK);

    /**
     * Puts one of the agent's queries onto the register, so a figure in an answer can be opened as
     * the recommendations behind it and exported the way any other search is.
     */
    /**
     * How this screen reaches the agent.
     *
     * <p>The conversation is still sent from here rather than kept on the server, which is the one
     * way this agent still differs from the safety view assistant. Moving it to the server's memory
     * is a change to how the agent answers, so it is not folded into this tidy-up.</p>
     */
    const agentClient: AgentClient = useMemo(() => ({
        ask: (question: string) => safetyRecommendationService.ask(question, agentTurns.flatMap((turn) => [
            {fromUser: true, text: turn.question},
            {fromUser: false, text: turn.investigation.answer ?? ''},
        ])),
        progress: (id: string) => safetyRecommendationService.askProgress(id),
    }), [agentTurns]);

    /** A step whose query the register can take is offered as a button under that step. */
    const renderShowInRegister = useCallback((step: StepDTO): React.ReactNode => {
        if (!step.detail) {
            return null;
        }
        return (
            <Tooltip title="Filter the register to these recommendations" describeChild>
                <Button
                    size="small"
                    startIcon={<FilterAltIcon fontSize="small" />}
                    onClick={(): void => filterToStep(step.detail as SafetyRecommendationStepFilter)}
                >
                    Show in register
                </Button>
            </Tooltip>
        );
    }, []);

    const filterToStep = (filter: SafetyRecommendationStepFilter): void => {
        setFilters({
            text: filter.text ?? '',
            statusGroups: filter.statusGroups ?? [],
            taxonomyLeafIds: filter.classificationIds ?? [],
            issuedFromYear: filter.fromYear ?? null,
            issuedToYear: filter.toYear ?? null,
            addressedTo: (filter.addressedTo as RegisterFilters['addressedTo']) ?? 'ANY',
            page: 0,
        });
        setChatOpen(false);
        setChatFullscreen(false);
    };

    /**
     * The chains the NTSB itself declared, fetched when one of the two panels is opened.
     *
     * <p>A few hundred rows against nearly six thousand recommendations, so they come whole rather
     * than paged, and are not held once closed: they change only when a sync runs.</p>
     */
    const openChains = (kind: DeclaredChainKind): void => {
        setChainsKind(kind);
        setChains(null);
        setChainsError(null);
        safetyRecommendationService.declaredChains(kind === 'supersessions')
            .then(setChains)
            .catch((failure: Error): void => setChainsError(failure.message || 'Failed to load the chains'));
    };

    /** A cell filters the table to that classification and that decade, and closes the grid. */
    const filterToCell = (leafId: number, decade: number): void => {
        setFilters({taxonomyLeafIds: [leafId], issuedFromYear: decade, issuedToYear: decade + 9, page: 0});
        setTopicOpen(false);
    };

    // The correspondence dialog: its recommendation, and its letters once loaded
    const [correspondenceFor, setCorrespondenceFor] = useState<SafetyRecommendationDTO | null>(null);
    const [letters, setLetters] = useState<SafetyRecommendationLetterDTO[] | null>(null);
    const [lettersError, setLettersError] = useState<string | null>(null);
    const openingFor = useRef<number | null>(null);
    const openCorrespondence = (rec: SafetyRecommendationDTO): void => {
        openingFor.current = rec.id;
        setCorrespondenceFor(rec);
        setLetters(null);
        setLettersError(null);
        safetyRecommendationService.getCorrespondence(rec)
            .then((loaded): void => { if (openingFor.current === rec.id) setLetters(loaded); })
            .catch((failure: Error): void => { if (openingFor.current === rec.id) setLettersError(failure.message); });
    };
    const closeCorrespondence = (): void => {
        openingFor.current = null;
        setCorrespondenceFor(null);
    };

    /** What matched. How few investigations issued them is said once, under the overview's count. */
    const matchedLine = `${total} recommendation${total === 1 ? '' : 's'} match`;

    /** The words the rows highlight: what was searched, not what is being typed. */
    const searchTerms = useMemo((): string[] => debouncedText.split(/\s+/).filter((word) => word.length > 0),
        [debouncedText]);

    const openRecommendation = (rec: SafetyRecommendationDTO): void =>
        setCurrentWorkspaceObject({objectType: ResourceType.SAFETY_RECOMMENDATION, object: rec});

    /** Opens one by id, for the places that hold a reference to a recommendation rather than all of it. */
    const openRecommendationById = (id: number): void => {
        safetyRecommendationService.get(id)
            .then(openRecommendation)
            .catch((failure: Error): void => setError(failure.message || 'Failed to open the recommendation'));
    };

    const statusCounts = useMemo((): Record<SafetyRecommendationStatusGroup, number> => {
        const counts = emptyGroupRecord((): number => 0);
        STATUS_GROUP_ORDER.forEach((group) => { counts[group] = stats?.byStatusGroup[group] ?? 0; });
        return counts;
    }, [stats]);
    const byYear = useMemo((): IssuedByYear => countsToIssuedByYear(stats?.byIssuedYear ?? []), [stats]);

    // No context blob and no "the search changed" notice: the agent queries every recommendation
    // held rather than reading the page, so what is on screen no longer bounds what it can answer.

    const toggleStatus = (group: SafetyRecommendationStatusGroup): void => setFilters({
        statusGroups: filters.statusGroups.includes(group)
            ? filters.statusGroups.filter((g) => g !== group)
            : [...filters.statusGroups, group],
    });

    // A single issue year is "selected" when the year range is exactly that year.
    const selectedYear = filters.issuedFromYear !== null && filters.issuedFromYear === filters.issuedToYear
        ? filters.issuedFromYear : null;
    const toggleYear = (year: number): void => {
        const clear = selectedYear === year;
        setFilters({issuedFromYear: clear ? null : year, issuedToYear: clear ? null : year});
    };

    // Compared against the sort in force rather than the stored one: with the default resolving to
    // issue date, clicking Issued once would otherwise ask for the order already on screen.
    const toggleSort = (nextSort: RegisterFilters['sortBy']): void => setFilters({
        sortBy: nextSort,
        sortDirection: effectiveSort === nextSort && filters.sortDirection === 'desc' ? 'asc' : 'desc',
    });

    const pager = (): React.ReactElement => (
        <TablePagination
            component="div"
            count={total}
            page={filters.page}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            onPageChange={(_, next): void => setFilters({page: next})}
            sx={{'& .MuiTablePagination-toolbar': {minHeight: 40, pl: 0}}}
        />
    );

    const yearInput = (value: number | null, onChange: (year: number | null) => void, label: string): React.ReactElement => (
        <TextField
            size="small"
            label={label}
            value={value ?? ''}
            inputProps={{inputMode: 'numeric', maxLength: 4}}
            onChange={(e): void => {
                const digits = e.target.value.replace(/\D/g, '');
                onChange(digits ? Number(digits) : null);
            }}
            sx={{width: 104}}
        />
    );

    return (
        // The outer box anchors the floating chat and its button; the inner one scrolls the page when the window is short
        <Box sx={{position: 'relative', height: '100%', minHeight: 0}}>
            <Box
                data-testid="recommendation-register-scroll"
                sx={{
                    height: '100%', overflowY: 'auto', boxSizing: 'border-box',
                    display: 'flex', flexDirection: 'column', gap: 1.25, p: 1.5,
                }}
            >
                {/* Everything the search matches, at a glance; both charts double as filters */}
                <RecommendationOverview
                    shownCount={total}
                    totalCount={total}
                    investigationCount={stats?.investigationCount}
                    statusCounts={statusCounts}
                    byYear={byYear}
                    selectedStatuses={filters.statusGroups}
                    issuedFromYear={filters.issuedFromYear}
                    issuedToYear={filters.issuedToYear}
                    onToggleStatus={toggleStatus}
                    onToggleYear={toggleYear}
                />

                {/* Filters */}
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                    <TextField
                        size="small"
                        label="Search"
                        placeholder="Number, text, keywords, addressee"
                        value={filters.text}
                        onChange={(e): void => setFilters({text: e.target.value})}
                        sx={{flex: '1 1 200px', maxWidth: 320}}
                    />
                    <FormControl size="small" sx={{width: 230}}>
                        <InputLabel id="recommendation-status">FAA status</InputLabel>
                        <Select
                            labelId="recommendation-status"
                            label="FAA status"
                            value={statusSelection(filters.statusGroups)}
                            onChange={(e): void => {
                                if (e.target.value !== MIXED_STATUS) setFilters({statusGroups: groupsForSelection(e.target.value)});
                            }}
                            renderValue={(value: string): string => {
                                if (value === MIXED_STATUS) return filters.statusGroups.map((g) => STATUS_GROUP_LABELS[g]).join(', ');
                                if (value === ANY_STATUS) return 'Any status';
                                return STATUS_PRESETS.find((p) => p.key === value)?.label
                                ?? STATUS_GROUP_LABELS[value as SafetyRecommendationStatusGroup];
                            }}
                        >
                            <MenuItem value={ANY_STATUS}>Any status</MenuItem>
                            {STATUS_PRESETS.map((preset) => (
                                <MenuItem key={preset.key} value={preset.key}>{preset.label}</MenuItem>
                            ))}
                            <Divider />
                            {STATUS_GROUP_ORDER.map((group) => {
                                const {color, Icon} = STATUS_STYLE[group];
                                return (
                                    <MenuItem key={group} value={group}>
                                        <Icon sx={{fontSize: 18, color, mr: 1}} />
                                        {STATUS_GROUP_LABELS[group]}
                                    </MenuItem>
                                );
                            })}
                            {/* The value shown while the chart holds a combination with no name; not pickable */}
                            <MenuItem value={MIXED_STATUS} sx={{display: 'none'}}>Combination</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{width: 140}}>
                        <InputLabel id="recommendation-addressed-to">Addressed to</InputLabel>
                        <Select
                            labelId="recommendation-addressed-to"
                            label="Addressed to"
                            value={filters.addressedTo}
                            onChange={(e): void => setFilters({addressedTo: e.target.value as RegisterFilters['addressedTo']})}
                        >
                            <MenuItem value="ANY">Anyone</MenuItem>
                            <MenuItem value="FAA">The FAA</MenuItem>
                            <MenuItem value="NOT_FAA">Not the FAA</MenuItem>
                        </Select>
                    </FormControl>
                    {yearInput(filters.issuedFromYear, (year): void => setFilters({issuedFromYear: year}), 'Issued from')}
                    {yearInput(filters.issuedToYear, (year): void => setFilters({issuedToYear: year}), 'Issued to')}
                    <Autocomplete
                        multiple
                        size="small"
                        sx={{flex: '1 1 260px', maxWidth: 360}}
                        options={taxonomyOptions}
                        groupBy={(option): string => option.category}
                        getOptionLabel={(option): string => option.leaf}
                        isOptionEqualToValue={(a, b): boolean => a.leafId === b.leafId}
                        value={taxonomyOptions.filter((option) => filters.taxonomyLeafIds.includes(option.leafId))}
                        onChange={(_, chosen): void => setFilters({taxonomyLeafIds: chosen.map((option) => option.leafId)})}
                        renderOption={(props, option): React.ReactElement => (
                            <li {...props} key={option.leafId}>
                                <Box>
                                    <Typography variant="body2">{option.leaf}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {option.isamDefinition}
                                    </Typography>
                                </Box>
                            </li>
                        )}
                        renderInput={(params): React.ReactElement => (
                            <TextField {...params} label="Recommendation taxonomy" placeholder="Any" />
                        )}
                    />

                    <FormControlLabel
                        sx={{mr: 0}}
                        control={
                            <Switch
                                size="small"
                                checked={filters.includeSuperseded}
                                onChange={(e): void => setFilters({includeSuperseded: e.target.checked})}
                            />
                        }
                        label={<Typography variant="body2">Show superseded</Typography>}
                    />
                    <Button size="small" onClick={(): void => setFilters(DEFAULT_REGISTER_FILTERS)}>Reset</Button>

                    {/*
                      * Breaks the wrap here so the views always start a line of their own. They
                      * wrapped into whatever space the taxonomy picker left, which put them in a
                      * different place depending on how many classifications were chosen.
                      */}
                    <Box sx={{flexBasis: '100%', height: 0}} />

                    <Button size="small" onClick={(): void => setTopicOpen(true)}>Topic over time</Button>
                    <Button size="small" onClick={(): void => openChains('supersessions')}>NTSB supersessions</Button>
                    <Button size="small" onClick={(): void => openChains('reiterations')}>NTSB reiterations</Button>
                    <Button size="small" onClick={(): void => setRepeatsOpen(true)}>Inferred repeated asks</Button>
                </Stack>

                {error ? (
                    <Box sx={{p: 2}}>
                        <Typography color="error">{error}</Typography>
                        <Button size="small" variant="outlined" sx={{mt: 1.5}} onClick={(): void => setAttempt((n) => n + 1)}>
                            Retry
                        </Button>
                    </Box>
                ) : (
                    <>
                        {stats?.meaningSearchUnavailable && (
                            <Alert severity="info" sx={{py: 0}}>
                                Meaning search is unavailable, so these are the recommendations containing your words.
                            </Alert>
                        )}

                        {/* A pager above and below: the rows are tall, so whichever end of the page
                        you are at, the next page is to hand without scrolling back. */}
                        <Stack direction="row" alignItems="center" justifyContent="space-between" useFlexGap flexWrap="wrap">
                            <Typography variant="body2" color="text.secondary">
                                {matchedLine}
                            </Typography>
                            {pager()}
                        </Stack>

                        {/* Register: fixed columns, the recommendation takes the remaining width. It fills the space
                        left but never drops below a readable height: past that, the whole tab scrolls instead. */}
                        <TableContainer
                            data-testid="recommendation-register-table"
                            sx={{flex: `1 0 ${MIN_TABLE_HEIGHT}px`, minHeight: MIN_TABLE_HEIGHT, border: 1, borderColor: 'divider', borderRadius: 1}}
                        >
                            <Table size="small" stickyHeader aria-label="Safety recommendations" sx={{tableLayout: 'fixed', minWidth: 900}}>
                                <colgroup>
                                    <col />
                                    <col style={{width: 96}} />
                                    <col style={{width: 168}} />
                                    <col style={{width: 88}} />
                                    <col style={{width: 180}} />
                                </colgroup>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <TableSortLabel active={effectiveSort === 'number'} direction={filters.sortDirection}
                                                onClick={(): void => toggleSort('number')}>
                                                Recommendation
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={effectiveSort === 'issuedDate'} direction={filters.sortDirection}
                                                onClick={(): void => toggleSort('issuedDate')}>
                                                Issued
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>FAA status</TableCell>
                                        <TableCell>
                                            <TableSortLabel active={effectiveSort === 'daysOpen'} direction={filters.sortDirection}
                                                onClick={(): void => toggleSort('daysOpen')}>
                                                Time
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>Other addressees</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows === null && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                <CircularProgress size={24} sx={{my: 3}} />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {rows !== null && rows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5}>
                                                <Typography variant="body2" color="text.secondary">
                                                    No recommendations match these filters.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {(rows ?? []).map((rec) => {
                                        const years = ageYears(rec, today);
                                        return (
                                            <TableRow key={rec.id} hover sx={{verticalAlign: 'top'}}>
                                                <TableCell>
                                                    <RecommendationSummary
                                                        rec={rec}
                                                        searchTerms={searchTerms}
                                                        onOpenLetters={(): void => openCorrespondence(rec)}
                                                        onOpen={(): void => openRecommendation(rec)}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{whiteSpace: 'nowrap'}}>{rec.issuedDate}</TableCell>
                                                <TableCell>
                                                    <RecommendationStatusChip
                                                        group={rec.focusStatusGroup}
                                                        label={displayStatus(focusStatus(rec)) || 'Status unknown'}
                                                        onClick={(): void => openCorrespondence(rec)}
                                                        ariaLabel={`${displayStatus(focusStatus(rec)) || 'Status unknown'}: read the ${rec.number} correspondence`}
                                                    />
                                                    {!isAddressedToFaa(rec) && (
                                                        <Typography variant="caption" color="text.secondary" component="div">
                                                            Not addressed to the FAA (overall status)
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {years !== null && (
                                                        <>
                                                            <Typography variant="body2">{years.toFixed(1)} yrs</Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {isOpen(rec) ? 'open so far' : 'to close'}
                                                            </Typography>
                                                        </>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="caption">
                                                        {rec.otherAddressees.map((a) => a.name).join('; ')}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Stack direction="row" justifyContent="flex-end">
                            {pager()}
                        </Stack>
                    </>
                )}
            </Box>

            <CorrespondenceDialog
                recommendation={correspondenceFor}
                letters={letters}
                error={lettersError}
                onClose={closeCorrespondence}
            />

            {/* What the issuer itself recorded, as opposed to what resemblance finds */}
            <Dialog
                open={chainsKind !== null}
                onClose={(): void => setChainsKind(null)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    {chainsKind === 'supersessions' ? 'NTSB supersessions' : 'NTSB reiterations'}
                    <Typography variant="body2" color="text.secondary" component="div" sx={{mt: 0.5}}>
                        {chainsKind === 'supersessions'
                            ? 'Where the NTSB replaced a recommendation with a later one.'
                            : 'Where a later investigation\'s report repeated a recommendation the NTSB had already '
                              + 'made, so the NTSB found it still applied. A reiteration points at an investigation '
                              + 'rather than at a second recommendation, and the NTSB records no date on it, so '
                              + 'these have a count but no span.'}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers sx={{maxHeight: '70vh'}}>
                    {chainsKind && (
                        <DeclaredChains
                            kind={chainsKind}
                            declared={chains}
                            error={chainsError}
                            onOpen={(id: number): void => {
                                setChainsKind(null);
                                openRecommendationById(id);
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* The same ask, made again over the years */}
            <Dialog open={repeatsOpen} onClose={(): void => setRepeatsOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    Inferred repeated asks
                    <Typography variant="body2" color="text.secondary" component="div" sx={{mt: 0.5}}>
                        Not the NTSB&apos;s own record: these groups are computed here, from what the
                        recommendations ask for, as follows.
                    </Typography>
                    <Box component="ul" sx={{m: 0, mt: 0.5, pl: 2.5, color: 'text.secondary'}}>
                        <Typography component="li" variant="body2">
                            Each recommendation&apos;s ask is embedded as a 1536-dimension vector
                            (text-embedding-3-small). The accident summary is excluded, so grouping follows the
                            request rather than the accident behind it.
                        </Typography>
                        <Typography component="li" variant="body2">
                            Two recommendations are linked when the cosine similarity of their asks is 0.80 or
                            more. A group is everything transitively linked: if A matches B and B matches C, all
                            three are one group even where A and C fall below the threshold.
                        </Typography>
                        <Typography component="li" variant="body2">
                            0.80 was calibrated against the 558 pairs the NTSB itself marked as one recommendation
                            superseding another: those average 0.70 similarity, while none of 1,600 randomly drawn
                            pairs reached 0.80.
                        </Typography>
                        <Typography component="li" variant="body2">
                            The NTSB marks far fewer of its own recommendations as reiterated or superseded than
                            actually repeat, so its marks measure nothing about repetition. They check this grouping
                            instead: a group containing one is marked &quot;Issuer confirms&quot;. The NTSB&apos;s
                            own marks are listed under NTSB supersessions and NTSB reiterations.
                        </Typography>
                        <Typography component="li" variant="body2">
                            &quot;Not acted on&quot; counts recommendations the NTSB closed as unacceptable or that
                            remain open. Titles are written by a language model from each group&apos;s
                            recommendations, so a title is a summary rather than the NTSB&apos;s wording.
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{maxHeight: '70vh'}}>
                    {repeatsError ? (
                        <Typography color="error">{repeatsError}</Typography>
                    ) : (
                        <RepeatedAsks
                            groups={repeats}
                            order={repeatsOrder}
                            onOrderChange={setRepeatsOrder}
                            onExport={exportRepeats}
                            onLoadLetters={(member): Promise<SafetyRecommendationLetterDTO[]> =>
                                safetyRecommendationService.getCorrespondence({id: member.id, number: member.number})}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* The same kind of fix, decade by decade, and how much of it the addressee acted on */}
            <Dialog open={topicOpen} onClose={(): void => setTopicOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    Topic over time
                    <Typography variant="body2" color="text.secondary" component="div" sx={{mt: 0.5}}>
                        Each row is one classification from the NTSB&apos;s recommendation taxonomy: 48 kinds of
                        fix, such as training or recorder systems, grouped under their four categories. Each column
                        is the decade a recommendation was issued.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" component="div" sx={{mt: 0.5}}>
                        A cell&apos;s number is how many recommendations fall there; the deeper its shading, the
                        more of them the NTSB closed as unacceptable or left open. Click a cell to filter the table
                        below to that classification and decade.
                    </Typography>
                </DialogTitle>
                <DialogContent dividers sx={{maxHeight: '70vh'}}>
                    {topicError ? (
                        <Typography color="error">{topicError}</Typography>
                    ) : (
                        <TopicOverTime
                            summary={topicSummary}
                            categories={taxonomy?.categories ?? []}
                            onSelectCell={filterToCell}
                            selectedLeafIds={filters.taxonomyLeafIds}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {!chatOpen && (
                <Fab
                    variant="extended"
                    color="primary"
                    size="medium"
                    onClick={(): void => setChatOpen(true)}
                    sx={{position: 'absolute', right: 24, bottom: 24, zIndex: 4}}
                >
                    <AutoAwesomeIcon sx={{mr: 1}} />
                    Ask about these
                </Fab>
            )}

            {/*
              * Conversation, floating over the table. Kept mounted while hidden, and resized in place
              * rather than moved into a portal when it fills the window: re-parenting it would remount
              * it and abandon research still running.
              */}
            <Paper
                elevation={8}
                role="dialog"
                aria-label="Ask about these recommendations"
                data-testid="recommendation-chat-panel"
                sx={{
                    display: chatOpen ? 'flex' : 'none',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    ...(chatFullscreen
                        ? {position: 'fixed', inset: 0, width: '100%', height: '100%', borderRadius: 0, zIndex: 1300}
                        : {
                            position: 'absolute',
                            right: 24,
                            bottom: 24,
                            width: 'min(480px, calc(100% - 48px))',
                            height: 'min(640px, calc(100% - 48px))',
                            zIndex: 5,
                        }),
                }}
            >
                <AgentChatPanel
                    title="Ask about these recommendations"
                    suggestedPrompts={SUGGESTED_PROMPTS}
                    turns={agentTurns}
                    onTurnsChange={setAgentTurns}
                    client={agentClient}
                    renderStepAction={renderShowInRegister}
                    fullscreen={chatFullscreen}
                    onToggleFullscreen={(): void => setChatFullscreen(!chatFullscreen)}
                    headerActions={
                        <IconButton
                            size="small"
                            aria-label="Close the conversation"
                            onClick={(): void => {
                                setChatOpen(false);
                                setChatFullscreen(false);
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    }
                />
            </Paper>
        </Box>
    );
};

export default SafetyRecommendationSearchEditor;
